/**
 * AllItemsTable — cross-category item summary with sort, column filters, and ticket expand.
 * Props: categories
 */
import React, { useState, useMemo } from 'react';
import { fmt$, fmtN } from '../../../utils/formatUtils';
import { fmtTimeCst } from '../../../utils/cstUtils';
import { SortTh, useSortState, applySortStr, useColFilters, applyColFilters, FilterCell } from '../../../utils/tableUtils';
import { getTicketType, TICKET_TYPE_META, ALL_COL_DEFS } from '../squareDashboardUtils';
import TicketChips from './TicketChips';
import TableContextMenu, { useContextMenu } from './TableContextMenu';
import { downloadCSV } from '../../../utils/excelUtils';

export default function AllItemsTable({ categories }) {
  const [search,       setSearch]       = useState('');
  const [expandedItem, setExpandedItem] = useState(null);
  const [sortKey, sortDir, onSort]      = useSortState('qty');
  const [colFilters, setColFilter, clearFilters, hasActiveFilters] = useColFilters(ALL_COL_DEFS);

  const allItems = useMemo(() => {
    const map = {};
    categories.forEach(cat => {
      cat.items.forEach(item => {
        const k = item.name.toLowerCase();
        if (!map[k]) map[k] = {
          name: item.name, qty: 0, revenueCents: 0, discountCents: 0,
          returnQty: 0, returnRev: 0, cats: new Set(),
        };
        map[k].qty          += item.qty;
        map[k].revenueCents += item.revenueCents;
        map[k].discountCents+= item.discountCents;
        map[k].returnQty    += item.returnQty;
        map[k].returnRev    += parseFloat(item.returnRevenue || 0);
        map[k].cats.add(cat.name);
      });
    });
    return Object.values(map)
      .map(item => ({ ...item, catStr: [...item.cats].join(', ') }))
      .sort((a, b) => b.qty - a.qty || b.revenueCents - a.revenueCents);
  }, [categories]);

  const itemTktMap = useMemo(() => {
    const map = {};
    categories.forEach(cat => {
      (cat.ticketList || []).forEach(tkt => {
        (tkt.lines || []).forEach(line => {
          const k = line.name.toLowerCase();
          if (!map[k]) map[k] = [];
          if (!map[k].find(t => t.id === tkt.id))
            map[k].push({ id: tkt.id, shortId: tkt.shortId, ticketName: tkt.ticketName || '', time: tkt.time, status: tkt.status, discountCents: tkt.discountCents || 0, notes: Array.isArray(line.notes) ? line.notes.filter(Boolean) : [] });
        });
      });
    });
    return map;
  }, [categories]);

  const filtered = useMemo(() => {
    const q    = search.trim().toLowerCase();
    const base = q ? allItems.filter(i => i.name.toLowerCase().includes(q)) : allItems;
    return applySortStr(applyColFilters(base, colFilters, ALL_COL_DEFS), sortKey, sortDir);
  }, [allItems, search, colFilters, sortKey, sortDir]);

  const tot = useMemo(
    () => filtered.reduce(
      (a, i) => ({ qty: a.qty + i.qty, rev: a.rev + i.revenueCents, disc: a.disc + i.discountCents, rQty: a.rQty + i.returnQty }),
      { qty: 0, rev: 0, disc: 0, rQty: 0 }
    ),
    [filtered]
  );

  const [menuState, openMenu, closeMenu] = useContextMenu();
  const handleDownload = () => {
    const headers = ['Item', 'Category', 'Qty', 'Revenue', 'Discount', 'Returned Qty', 'Notes'];
    const rows = filtered.map(item => {
      const notes = [...new Set((itemTktMap[item.name.toLowerCase()] || [])
        .flatMap(t => Array.isArray(t.notes) ? t.notes : []))].filter(Boolean).join('; ');
      return [
        item.name, item.catStr, item.qty,
        (item.revenueCents / 100).toFixed(2),
        (item.discountCents / 100).toFixed(2),
        item.returnQty,
        notes,
      ];
    });
    downloadCSV(headers, rows, `all-items-${new Date().toISOString().slice(0,10)}`);
  };

  return (
    <>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
        <input
          className="sq__search"
          type="search"
          placeholder="Search all items…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {hasActiveFilters && (
          <button className="sq__filter-clear" onClick={clearFilters}>✕ Clear column filters</button>
        )}
        {(hasActiveFilters || search) && (
          <span className="sq__filter-bar-label">{filtered.length} of {allItems.length} items</span>
        )}
      </div>

      <div className="sq__table-wrap" onContextMenu={openMenu}>
        <TableContextMenu {...menuState} onClose={closeMenu} onDownload={handleDownload} />
        <table className="sq__table">
          <thead>
            <tr>
              <SortTh col="name"          sortKey={sortKey} sortDir={sortDir} onSort={onSort}>Item</SortTh>
              <SortTh col="catStr"        sortKey={sortKey} sortDir={sortDir} onSort={onSort}>Category</SortTh>
              <SortTh col="qty"           sortKey={sortKey} sortDir={sortDir} onSort={onSort} className="sq__num">Qty</SortTh>
              <SortTh col="revenueCents"  sortKey={sortKey} sortDir={sortDir} onSort={onSort} className="sq__num">Revenue</SortTh>
              <SortTh col="discountCents" sortKey={sortKey} sortDir={sortDir} onSort={onSort} className="sq__num">Discount</SortTh>
              <SortTh col="returnQty"     sortKey={sortKey} sortDir={sortDir} onSort={onSort} className="sq__num">Returned</SortTh>
              <th>Notes</th>
              <th>Tickets</th>
            </tr>
            <tr className="sq__filter-row">
              <FilterCell type="text"   fval={colFilters.name}          colKey="name"          setFilter={setColFilter} />
              <FilterCell type="text"   fval={colFilters.catStr}        colKey="catStr"        setFilter={setColFilter} />
              <FilterCell type="min"    fval={colFilters.qty}           colKey="qty"           setFilter={setColFilter} className="sq__num" />
              <FilterCell type="minmax" fval={colFilters.revenueCents}  colKey="revenueCents"  setFilter={setColFilter} className="sq__num" />
              <FilterCell type="min"    fval={colFilters.discountCents} colKey="discountCents" setFilter={setColFilter} className="sq__num" />
              <FilterCell type="min"    fval={colFilters.returnQty}     colKey="returnQty"     setFilter={setColFilter} className="sq__num" />
              <td />
              <td />
            </tr>
          </thead>
          <tbody>
            {filtered.map((item, i) => {
              const tkts  = itemTktMap[item.name.toLowerCase()] || [];
              const itemNotes = [...new Set(tkts.flatMap(t => Array.isArray(t.notes) ? t.notes : []))].filter(Boolean);
              const isExp = expandedItem === item.name;
              return (
                <React.Fragment key={i}>
                  <tr className={item.returnQty > 0 ? 'sq__row-return' : ''}>
                    <td className="sq__item-name">{item.name}</td>
                    <td className="sq__cats-cell">{item.catStr}</td>
                    <td className="sq__num">{fmtN(item.qty)}</td>
                    <td className="sq__num sq__revenue">{fmt$(item.revenueCents / 100)}</td>
                    <td className="sq__num sq__discount">{item.discountCents > 0 ? fmt$(item.discountCents / 100) : '—'}</td>
                    <td className="sq__num">{item.returnQty > 0 ? <span className="sq__return-badge">{item.returnQty}</span> : '—'}</td>
                    <td className="sq__note-cell">
                      {itemNotes.length > 0 ? (
                        <div className="sq__note-tags">
                          {itemNotes.map((n, ni) => <span key={ni} className="sq__note-tag" title={n}>📝 {n}</span>)}
                        </div>
                      ) : <span className="sq__muted">—</span>}
                    </td>
                    <td className="sq__itkt-cell">
                      <TicketChips
                        tickets={tkts}
                        expanded={isExp}
                        onExpand={() => setExpandedItem(isExp ? null : item.name)}
                      />
                    </td>
                  </tr>
                  {isExp && tkts.length > 2 && (
                    <tr className="sq__itkt-expand-row">
                      <td colSpan={8}>
                        <div className="sq__itkt-poplist">
                          {tkts.map((t, j) => {
                            const ttype = getTicketType(t);
                            return (
                              <div key={j} className="sq__itkt-poprow">
                                <span className={`sq__tkt-typebadge sq__tkt-typebadge--${ttype}`}>
                                  {TICKET_TYPE_META[ttype].label}
                                </span>
                                <span className="sq__itkt-popname">
                                  {t.ticketName || <span className="sq__muted">—</span>}
                                </span>
                                <span className="sq__itkt-poptime">{fmtTimeCst(t.time)}</span>
                              </div>
                            );
                          })}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="sq__tfoot">
              <td colSpan={2}>Total ({filtered.length} items)</td>
              <td className="sq__num">{fmtN(tot.qty)}</td>
              <td className="sq__num sq__revenue">{fmt$(tot.rev / 100)}</td>
              <td className="sq__num sq__discount">{tot.disc > 0 ? fmt$(tot.disc / 100) : '—'}</td>
              <td className="sq__num">{tot.rQty > 0 ? fmtN(tot.rQty) : '—'}</td>
              <td />
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
    </>
  );
}
